/**
 * Robust physical Shift / Cmd(Meta) modifier tracking for the workspace canvas.
 *
 * WHY THIS EXISTS (RFC — post 1573281354429162): the canvas drives pan-vs-select mode from a
 * single "is Shift physically held" boolean (`isShiftSelecting` → `panOnDrag={!isShiftSelecting}` /
 * `selectionOnDrag={isShiftSelecting}`). If we only toggled that boolean on Shift keydown/keyup, a
 * SINGLE missed Shift `keyup` would latch the canvas in selection mode forever — dragging empty
 * canvas would rubber-band-select instead of pan, and the only escape would be ReactFlow's built-in
 * Space pan (which reverts on release). Chromium drops a modifier `keyup` when the focused element is
 * disabled/unmounted mid-keypress (e.g. answering a Director MCQ disables then unmounts the focused
 * option button), which is exactly how users hit this.
 *
 * The fix (mirrors the Meta self-heal from RFC-0813 / PR #1815): reconcile BOTH modifiers from the
 * authoritative event state rather than trusting a matched keydown/keyup pair —
 *   - on every keydown/keyup, read `event.getModifierState('Shift'|'Meta')` (self-heals a missed
 *     keyup on the very next keystroke), and
 *   - on every pointerdown, read `event.shiftKey` (self-heals from a plain click WITHOUT breaking
 *     Shift+drag box-select: genuinely holding Shift keeps selection on). React batches this native
 *     update, so recovery reliably takes effect for the FOLLOWING gesture; one plain click un-sticks
 *     it permanently.
 * Plus window blur / tab-hide resets. Handlers fire ONLY when the tracked value actually changes.
 *
 * Extracted from WorkspacePageV2 so the recovery behavior is unit-testable with real DOM events.
 */

export type ModifierChangeReason =
  | 'keydown'
  | 'keyup'
  | 'pointerdown'
  | 'blur'
  | 'visibilitychange'

export interface ShiftMetaTrackingHandlers {
  /** Called when the physical Shift-held state changes. `event` is present for key/pointer sources. */
  onShiftHeldChange: (
    held: boolean,
    info: { reason: ModifierChangeReason; event?: KeyboardEvent | PointerEvent },
  ) => void
  /** Called when the physical Cmd/Meta-held state changes. */
  onMetaHeldChange: (held: boolean, info: { reason: ModifierChangeReason }) => void
}

/**
 * Attach the modifier trackers to `target` (normally `window`). Returns a cleanup function that
 * removes every listener. Idempotent per install; safe to call from a React effect.
 */
export function installShiftMetaTracking(
  target: Window,
  handlers: ShiftMetaTrackingHandlers,
): () => void {
  let shiftHeld = false
  let metaHeld = false

  const setShift = (
    held: boolean,
    reason: ModifierChangeReason,
    event?: KeyboardEvent | PointerEvent,
  ) => {
    if (shiftHeld === held) return
    shiftHeld = held
    handlers.onShiftHeldChange(held, { reason, event })
  }
  const setMeta = (held: boolean, reason: ModifierChangeReason) => {
    if (metaHeld === held) return
    metaHeld = held
    handlers.onMetaHeldChange(held, { reason })
  }

  const onKeyDown = (event: KeyboardEvent) => {
    setMeta(event.getModifierState('Meta'), 'keydown')
    setShift(event.getModifierState('Shift'), 'keydown', event)
  }
  const onKeyUp = (event: KeyboardEvent) => {
    setMeta(event.getModifierState('Meta'), 'keyup')
    setShift(event.getModifierState('Shift'), 'keyup', event)
  }
  const onPointerDown = (event: PointerEvent) => {
    // Only Shift needs pointer-based recovery — a stale Shift latch breaks panning, which is what
    // users report. (Meta already self-heals on the next keystroke and never gates panning.)
    setShift(event.shiftKey, 'pointerdown', event)
  }
  const onBlur = () => {
    setShift(false, 'blur')
    setMeta(false, 'blur')
  }
  const onVisibilityChange = () => {
    if (target.document.hidden) {
      setShift(false, 'visibilitychange')
      setMeta(false, 'visibilitychange')
    }
  }

  target.addEventListener('keydown', onKeyDown)
  target.addEventListener('keyup', onKeyUp)
  // Capture phase runs before ReactFlow's pane pointer handler, but the resulting React re-render is
  // batched — so the reconciled panOnDrag reliably applies to the FOLLOWING gesture, not necessarily
  // the current pointerdown. That still fully un-sticks the canvas after one plain click.
  target.addEventListener('pointerdown', onPointerDown, true)
  target.addEventListener('blur', onBlur)
  target.document.addEventListener('visibilitychange', onVisibilityChange)

  return () => {
    target.removeEventListener('keydown', onKeyDown)
    target.removeEventListener('keyup', onKeyUp)
    target.removeEventListener('pointerdown', onPointerDown, true)
    target.removeEventListener('blur', onBlur)
    target.document.removeEventListener('visibilitychange', onVisibilityChange)
  }
}
